import React, { useEffect, useState } from 'react';
import { FaCalendarAlt, FaClock, FaMountain, FaRoad } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { HiFlag, HiSparkles } from 'react-icons/hi2';
import { formatDurationMin } from '../../utils/duration';
import { eventDates } from '../../utils/plan_changes';

function getDayOfMonth(isoDate) {
  if (!isoDate) return null;
  const parts = String(isoDate).split('-');
  const day = Number(parts[2]);
  return Number.isFinite(day) ? day : null;
}

function formatDayMonth(isoDate) {
  if (!isoDate) return null;
  const parts = String(isoDate).split('-');
  if (parts.length !== 3) return null;
  const dd = parts[2];
  const mm = parts[1];
  if (!dd || !mm) return null;
  return `${dd}/${mm}`;
}

function isoToDate(iso) {
  try {
    return iso ? new Date(`${iso}T00:00:00`) : null;
  } catch (e) {
    return null;
  }
}

function dateToIsoLocal(d) {
  if (!d || Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function addDaysIso(isoDate, deltaDays) {
  const d = isoToDate(isoDate);
  if (!d || Number.isNaN(d.getTime()) || !Number.isFinite(Number(deltaDays))) return null;
  d.setDate(d.getDate() + Number(deltaDays));
  return dateToIsoLocal(d);
}

function getIsoDayIndexMondayFirst(isoDate) {
  const d = isoToDate(isoDate);
  if (!d || Number.isNaN(d.getTime())) return null;
  // JS: 0=Sun..6=Sat => Monday-first: 0=Mon..6=Sun
  return (d.getDay() + 6) % 7;
}

function EventFlag({ className = '' }) {
  return (
    <span className={`absolute -top-1 -left-1 w-4 h-4 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-sm ring-1 ring-white ${className}`}>
      <HiFlag className="w-2.5 h-2.5" />
    </span>
  );
}

const BUBBLE_METRIC_LS_KEY = 'runcoach:compactCalendarBubbleMetric';

function MetricToggle({ value, onChange }) {
  const base =
    'w-8 h-8 rounded-full flex items-center justify-center transition border cursor-pointer';
  const iconClass = 'w-3.5 h-3.5';

  const active = 'bg-gray-900 text-white border-gray-900 shadow-md';
  const inactive = 'bg-white text-gray-400 border-gray-200 hover:border-gray-300 hover:text-gray-600';

  return (
    <div className="flex items-center justify-end gap-1.5 bg-gray-50 p-1 rounded-full border border-gray-100">
      <button
        type="button"
        onClick={() => onChange('km')}
        className={`${base} ${value === 'km' ? active : inactive}`}
        aria-label="Show distance in bubbles"
        title="Distance (km)"
      >
        <FaRoad className={iconClass} />
      </button>

      <button
        type="button"
        onClick={() => onChange('elev')}
        className={`${base} ${value === 'elev' ? active : inactive}`}
        aria-label="Show elevation gain in bubbles"
        title="Elevation (m)"
      >
        <FaMountain className={iconClass} />
      </button>

      <button
        type="button"
        onClick={() => onChange('min')}
        className={`${base} ${value === 'min' ? active : inactive}`}
        aria-label="Show duration in bubbles"
        title="Duration (min)"
      >
        <FaClock className={iconClass} />
      </button>

      <button
        type="button"
        onClick={() => onChange('date')}
        className={`${base} ${value === 'date' ? active : inactive}`}
        aria-label="Show date in bubbles"
        title="Date (DD/MM)"
      >
        <FaCalendarAlt className={iconClass} />
      </button>
    </div>
  );
}

export default function CompactTrainingCalendar({
  weeks,
  days,
  dayInitials,
  todayDate,
  todayLabel,
  workoutColors,
  selectedWorkoutId = null,
  selectedDateISO = null,
  getWorkoutForDay,
  getActualStats,
  getCompletedId,
  getSkippedId,
  getWeekPlannedStats,
  onWorkoutPress,
  onMultipleWorkoutsPress = null,
  onTodayPress = null,
  renderWeekMeta = null,
  legendItems = null,
  events = null,
  onEventPress = null
}) {
  const { t } = useTranslation();
  const eventList = Array.isArray(events) ? events.filter((e) => e && e.start_date) : [];
  const eventsByDate = eventList.reduce((acc, ev) => {
    eventDates(ev).forEach((iso) => {
      if (!acc[iso]) acc[iso] = [];
      acc[iso].push(ev);
    });
    return acc;
  }, {});
  const eventById = eventList.reduce((acc, ev) => {
    acc[String(ev.id)] = ev;
    return acc;
  }, {});
  const hasWeekMeta = typeof renderWeekMeta === 'function';
  const [bubbleMetric, setBubbleMetric] = useState(() => {
    try {
      const v = localStorage.getItem(BUBBLE_METRIC_LS_KEY);
      return v === 'elev' || v === 'min' || v === 'km' || v === 'date' ? v : 'km';
    } catch (e) {
      return 'km';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(BUBBLE_METRIC_LS_KEY, bubbleMetric);
    } catch (e) {
      // ignore
    }
  }, [bubbleMetric]);

  return (
    <div className="w-full">
      {/* Title and metric toggle */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <MetricToggle value={bubbleMetric} onChange={setBubbleMetric} />
      </div>

      {/* Day initials */}
      <div
        className={[
          'grid gap-3 items-end mb-4 border-b border-gray-100 pb-2',
          hasWeekMeta ? 'grid-cols-[1fr_56px] md:grid-cols-[120px_1fr_56px]' : 'grid-cols-[1fr_56px]'
        ].join(' ')}
      >
        {hasWeekMeta ? (
          <div className="hidden md:block text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">
            Week
          </div>
        ) : null}
        <div className="grid grid-cols-7 gap-1.5">
          {dayInitials.map((label, idx) => (
            <div
              key={`${label}-${idx}`}
              className="text-center text-[10px] font-black text-gray-400 uppercase tracking-widest"
            >
              {label}
            </div>
          ))}
        </div>
        <div className="text-right text-[10px] font-black text-gray-400 uppercase tracking-widest pr-1">
          Total
        </div>
      </div>

      <div className="space-y-4">
        {weeks.map((week) => {
          const planned = getWeekPlannedStats(week.workouts);
          const km = Math.round(planned.km || 0);
          const elev = Math.round(planned.elev || 0);
          const weekDates = (week.workouts || []).map(w => w.date).filter(Boolean).sort();
          const weekStart = weekDates[0] || null;
          const weekEnd = weekDates[weekDates.length - 1] || null;
          const weekStartIdx = weekStart ? getIsoDayIndexMondayFirst(weekStart) : null;
          const weekStartMonday = (weekStart && weekStartIdx !== null) ? addDaysIso(weekStart, -weekStartIdx) : null;
          const weekSunday = weekStartMonday ? addDaysIso(weekStartMonday, 6) : null;
          const weekEvents = weekStartMonday
            ? eventList.filter((ev) => String(ev.start_date) <= String(weekSunday) && String(ev.end_date || ev.start_date) >= String(weekStartMonday))
            : [];

          return (
            <React.Fragment key={week.week_number}>
            <div
              className={[
                'grid gap-3 items-center py-1',
                hasWeekMeta ? 'grid-cols-[1fr_56px] md:grid-cols-[120px_1fr_56px]' : 'grid-cols-[1fr_56px]'
              ].join(' ')}
            >
              {hasWeekMeta ? (
                <div className="hidden md:flex items-center">
                  {renderWeekMeta(week)}
                </div>
              ) : null}
              <div className="grid grid-cols-7 gap-1.5">
                {days.map((dayFull, dayIdx) => {
                  const workoutsForDay = typeof getWorkoutForDay === 'function' ? getWorkoutForDay(week.workouts, dayFull) : null;
                  const rawWorkouts = Array.isArray(workoutsForDay) ? workoutsForDay : (workoutsForDay ? [workoutsForDay] : []);

                  // We usually hide rest days in the compact calendar, but if a rest day carries a "missed" marker
                  // (e.g. "Rest (missed optional jog)"), we render it so the user can see the missed indicator.
                  const renderableWorkouts = rawWorkouts.filter((w) => {
                    if (!w) return false;
                    const isRest = w?.type === 'rest';
                    const skippedId = w && getSkippedId ? getSkippedId(w) : null;
                    return !isRest || !!skippedId;
                  });

                  const cellDateISO = rawWorkouts?.[0]?.date || (weekStartMonday ? addDaysIso(weekStartMonday, dayIdx) : null);
                  const isToday = Boolean(todayDate && cellDateISO && String(cellDateISO) === String(todayDate));

                  // Multi workouts per day: show a dedicated bubble with #count.
                  if (renderableWorkouts.length > 1) {
                    const count = renderableWorkouts.length;
                    const doneCount = renderableWorkouts.filter((w) => w?.completed === true).length;
                    const missedCount = renderableWorkouts.filter((w) => {
                      const skippedId = w && getSkippedId ? getSkippedId(w) : null;
                      return (Boolean(skippedId) || Boolean(w?.skipped)) && w?.completed !== true;
                    }).length;

                    const isSelected = Boolean(
                      (selectedWorkoutId && renderableWorkouts.some((w) => w?.id && String(w.id) === String(selectedWorkoutId))) ||
                        (selectedDateISO && cellDateISO && String(selectedDateISO) === String(cellDateISO))
                    );

                    const ringClass =
                      doneCount === count
                        ? 'ring-2 ring-green-500 ring-offset-2'
                        : (missedCount > 0 ? 'ring-2 ring-orange-400 ring-offset-2' : '');

                    const title = renderableWorkouts
                      .map((w) => w?.title || '(Untitled)')
                      .filter(Boolean)
                      .slice(0, 6)
                      .join(' · ');

                    return (
                      <button
                        key={`${week.week_number}-${dayFull}`}
                        type="button"
                        onClick={() => {
                          if (typeof onMultipleWorkoutsPress === 'function') {
                            onMultipleWorkoutsPress({
                              weekNumber: week.week_number,
                              dayName: dayFull,
                              date: cellDateISO,
                              workouts: renderableWorkouts
                            });
                            return;
                          }
                          const first = renderableWorkouts[0];
                          if (first && typeof onWorkoutPress === 'function') onWorkoutPress(first, week.week_number);
                        }}
                        className="w-full flex items-center justify-center cursor-pointer group"
                        aria-label={`#${count} workouts`}
                      >
                        <div
                          className={[
                            'relative rounded-full flex items-center justify-center select-none transition-all duration-200',
                            'w-full max-w-[44px] aspect-square',
                            'bg-gray-900 text-white shadow-md group-hover:shadow-lg group-hover:-translate-y-0.5',
                            isToday ? 'ring-2 ring-indigo-500 ring-offset-2' : '',
                            ringClass,
                            isSelected ? 'ring-2 ring-indigo-600 ring-offset-2 scale-110' : '',
                            'active:scale-[0.95]'
                          ].join(' ')}
                          title={title}
                        >
                          <span className="text-[11px] font-black tabular-nums">#{count}</span>
                          {cellDateISO && eventsByDate[cellDateISO] ? <EventFlag /> : null}
                          {renderableWorkouts.some((w) => w?.added_by === 'coach') ? (
                            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white text-indigo-600 flex items-center justify-center shadow-sm ring-1 ring-indigo-200">
                              <HiSparkles className="w-2.5 h-2.5" />
                            </span>
                          ) : null}
                        </div>
                      </button>
                    );
                  }

                  const workout = renderableWorkouts[0] || null;
                  const skippedId = workout && getSkippedId ? getSkippedId(workout) : null;
                  const hasRenderableWorkout = !!workout;
                  const isDone = workout?.completed === true;
                  const isMissed = (Boolean(skippedId) || Boolean(workout?.skipped)) && !isDone;

                  // Base bubble color remains sport/type-driven. Missed state is conveyed via orange ring + orange × badge.
                  const colorClass = hasRenderableWorkout
                    ? (workoutColors[workout.type] || 'bg-gray-300')
                    : 'bg-gray-50 border border-gray-100';
                  const textClass = hasRenderableWorkout
                    ? (!['rest', 'recovery'].includes(workout.type) ? 'text-white' : 'text-gray-800')
                    : 'text-gray-300';

                  const completedId = hasRenderableWorkout ? getCompletedId(workout) : null;

                  const isSelected = Boolean(
                    (hasRenderableWorkout &&
                      selectedWorkoutId &&
                      workout?.id &&
                      String(workout.id) === String(selectedWorkoutId)) ||
                      (selectedDateISO && cellDateISO && String(selectedDateISO) === String(cellDateISO))
                  );
                  const metricValue = hasRenderableWorkout
                    ? (bubbleMetric === 'km'
                        ? (workout?.distance_km ?? null)
                        : (bubbleMetric === 'elev'
                            ? (workout?.altitude_gain_m ?? null)
                            : (bubbleMetric === 'min'
                                ? (workout?.duration_min ?? null)
                                : null)))
                    : null;
                  
                  let label = null;
                  if (metricValue !== null && metricValue !== undefined && Number.isFinite(Number(metricValue)) && !(workout?.type === 'rest')) {
                    const val = Number(metricValue);
                    // For small values (swim, short runs), show 1 decimal if needed
                    if (val < 10 && val > 0) {
                      label = parseFloat(val.toFixed(1));
                    } else {
                      label = Math.round(val);
                    }
                  }
                  const dom = hasRenderableWorkout ? getDayOfMonth(workout?.date) : null;
                  const ddmm = hasRenderableWorkout ? formatDayMonth(workout?.date) : null;
                  const durationText = hasRenderableWorkout
                    ? formatDurationMin(workout?.duration_min, { showUnitForMinutes: false })
                    : null;
                  const bubbleText = bubbleMetric === 'date'
                    ? (ddmm ?? '?')
                    : (bubbleMetric === 'min'
                        ? (durationText ?? '?')
                        : (label ?? (bubbleMetric === 'km' ? (dom ?? '?') : '?')));

                  // No workout entry: keep spacing, and optionally show a Today placeholder
                  if (!hasRenderableWorkout) {
                    const isSelectedPlaceholder = Boolean(
                      selectedDateISO && cellDateISO && String(selectedDateISO) === String(cellDateISO)
                    );
                    const canAttachPastWorkout = Boolean(
                      typeof onTodayPress === 'function' &&
                        todayDate &&
                        cellDateISO &&
                        String(cellDateISO) < String(todayDate)
                    );
                    const emptyDayLabel = bubbleMetric === 'date'
                      ? (formatDayMonth(cellDateISO) ?? '')
                      : (getDayOfMonth(cellDateISO) ?? '');
                    const cellEvents = cellDateISO ? (eventsByDate[cellDateISO] || []) : [];
                    if (cellEvents.length > 0 && !isToday) {
                      const ev = cellEvents[0];
                      return (
                        <div key={`${week.week_number}-${dayFull}`} className="w-full flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => (typeof onEventPress === 'function' ? onEventPress(ev) : null)}
                            className="relative w-full max-w-[44px] aspect-square rounded-full border-2 border-dashed border-indigo-300 bg-indigo-50 text-indigo-600 flex items-center justify-center cursor-pointer hover:-translate-y-0.5 transition-all"
                            title={ev.title}
                            aria-label={ev.title}
                          >
                            <HiFlag className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div key={`${week.week_number}-${dayFull}`} className="w-full flex items-center justify-center">
                        {isToday ? (
                          typeof onTodayPress === 'function' ? (
                            <button
                              type="button"
                              onClick={() => onTodayPress({ weekNumber: week.week_number, dayName: dayFull, date: todayDate })}
                              className={[
                                'w-full max-w-[44px] aspect-square rounded-full border-2 flex items-center justify-center transition-all duration-200',
                                'cursor-pointer hover:-translate-y-0.5 shadow-sm hover:shadow-md',
                                isSelectedPlaceholder ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-indigo-200 bg-white text-indigo-500 hover:border-indigo-400'
                              ].join(' ')}
                              aria-label={todayLabel || 'Today'}
                              title={todayLabel || 'Today'}
                            >
                              <span className="text-[9px] font-black uppercase tracking-wider">
                                {todayLabel || 'Today'}
                              </span>
                            </button>
                          ) : (
                            <div className="w-full max-w-[44px] aspect-square rounded-full border-2 border-indigo-200 bg-indigo-50 flex items-center justify-center text-indigo-500 shadow-inner">
                            <span className="text-[9px] font-black uppercase tracking-wider">
                              {todayLabel || 'Today'}
                            </span>
                          </div>
                          )
                        ) : canAttachPastWorkout ? (
                          <button
                            type="button"
                            onClick={() => onTodayPress({ weekNumber: week.week_number, dayName: dayFull, date: cellDateISO })}
                            className={[
                              'w-full max-w-[44px] aspect-square rounded-full border border-dashed flex items-center justify-center transition-all duration-200',
                              'bg-gray-50/50 text-gray-400 border-gray-200 hover:bg-white hover:border-gray-300 hover:shadow-sm',
                              'cursor-pointer',
                              isSelectedPlaceholder ? 'ring-2 ring-indigo-500 ring-offset-1 bg-indigo-50/50 text-indigo-500 border-indigo-200' : ''
                            ].join(' ')}
                            aria-label={`Add workout on ${cellDateISO}`}
                            title={`Add workout on ${cellDateISO}`}
                          >
                            <span className="text-[10px] font-bold tabular-nums opacity-60">
                              {emptyDayLabel}
                            </span>
                          </button>
                        ) : (
                          <div className="w-full max-w-[44px] aspect-square" />
                        )}
                      </div>
                    );
                  }

                  return (
                    <button
                      key={`${week.week_number}-${dayFull}`}
                      type="button"
                      onClick={() => workout && onWorkoutPress(workout, week.week_number)}
                      disabled={!workout}
                      className="w-full flex items-center justify-center cursor-pointer group"
                      aria-label={workout ? `${workout.title}` : `${dayFull}`}
                    >
                      <div
                        className={[
                          'relative rounded-full flex items-center justify-center select-none transition-all duration-200',
                          'w-full max-w-[44px] aspect-square',
                          workout ? colorClass : 'bg-transparent',
                          workout ? 'shadow-md group-hover:shadow-lg group-hover:-translate-y-0.5' : '',
                          textClass,
                          isToday ? 'ring-2 ring-indigo-500 ring-offset-2' : '',
                          isDone ? 'ring-2 ring-green-500 ring-offset-2' : '',
                          isMissed ? 'ring-2 ring-orange-400 ring-offset-2' : '',
                          // Selected workout (must stack with today/done rings)
                          isSelected ? 'ring-2 ring-indigo-600 ring-offset-2 scale-110 z-10' : '',
                          workout ? 'active:scale-[0.95]' : ''
                        ].join(' ')}
                        title={workout
                          ? [
                              workout.event_id && eventById[String(workout.event_id)] ? `🏁 ${eventById[String(workout.event_id)].title}` : null,
                              workout.added_by === 'coach' ? t('events.added_by_coach') : null,
                              `${workout.title}: ${workout.description}`
                            ].filter(Boolean).join(' — ')
                          : undefined}
                      >
                        {/* If we have a distance, show it; otherwise show the day of month */}
                        <span
                          className={[
                            'font-black drop-shadow-sm tabular-nums',
                            bubbleMetric === 'date' ? 'text-[9px]' : 'text-[10px]'
                          ].join(' ')}
                        >
                          {workout ? bubbleText : ''}
                        </span>

                        {/* Today marker (workout) */}
                        {workout && isToday && (
                          <span className="absolute bottom-1 left-1 w-1.5 h-1.5 rounded-full bg-indigo-500 ring-1 ring-white" />
                        )}

                        {/* Completion indicator */}
                        {isDone && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-500 text-white text-[9px] font-black flex items-center justify-center shadow-sm ring-1 ring-white">
                            ✓
                          </span>
                        )}

                        {!isDone && isMissed && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-orange-500 text-white text-[10px] font-black flex items-center justify-center shadow-sm ring-1 ring-white">
                            ×
                          </span>
                        )}

                        {/* Event day (the event itself, or a day covered by an event) */}
                        {(workout?.event_id || (cellDateISO && eventsByDate[cellDateISO])) ? <EventFlag /> : null}

                        {/* Session added by the coach */}
                        {workout?.added_by === 'coach' && !workout?.event_id ? (
                          <span
                            className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white text-indigo-600 flex items-center justify-center shadow-sm ring-1 ring-indigo-200"
                            title={t('events.added_by_coach')}
                          >
                            <HiSparkles className="w-2.5 h-2.5" />
                          </span>
                        ) : null}

                        {/* Completed workout id (hint for accessibility / debugging) */}
                        {completedId ? (
                          <span className="sr-only">Completed</span>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Weekly summary */}
              <div className="flex flex-col items-end justify-center pr-1 opacity-60">
                <div className="text-[10px] font-black text-gray-900 tabular-nums">{km}km</div>
                <div className="text-[9px] font-bold text-gray-500 tabular-nums">+{elev}m</div>
              </div>
            </div>
            {weekEvents.length > 0 ? (
              <div className={hasWeekMeta ? 'md:pl-[132px] -mt-2' : '-mt-2'}>
                <div className="flex flex-wrap gap-1.5">
                  {weekEvents.map((ev) => (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => (typeof onEventPress === 'function' ? onEventPress(ev) : null)}
                      className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-[10px] font-black text-indigo-700 hover:bg-indigo-100 transition-colors"
                      title={ev.title}
                    >
                      <HiFlag className="w-3 h-3 shrink-0" />
                      <span className="truncate">{ev.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            </React.Fragment>
          );
        })}
      </div>

      {/* Legend (below calendar) */}
      <div className="mt-8 pt-6 pb-8 border-t border-gray-100 flex flex-wrap justify-center gap-2">
        {(Array.isArray(legendItems) ? legendItems : []).map((it, idx) => (
          <div key={`${it?.label || 'item'}-${idx}`} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-50 border border-gray-100">
            <div className={`w-2 h-2 rounded-full shadow-sm ${it?.colorClass || 'bg-gray-300'}`} />
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">{it?.label || ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


